import { IYearMonth } from '@/coreDomainsModule/date';
import { OutputForecastForReceiptExcelData, OutputForecastForReceiptBusinessData } from '../domains/entities/forecastForReceiptResponse';
import JSZip from 'jszip';
import XlsxPopulate from 'xlsx-populate';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { i18n } from '@/i18n';

/**
 * B列～L列などに対応する列マッピング用インターフェース
 */
interface IColumnMapping {
  dataKey: keyof OutputForecastForReceiptBusinessData; // データのキー
  colIndex: number; // 1-basedの列番号 (B列=2, C列=3, ...)
  headerLabel: string; // ヘッダー文字列(必要なら)
  headerRow?: number; // ヘッダーを書く行(必要なら)
}

const t = i18n.t;

// xlsx-populate はテンプレートのテーマカラーを読み取れないため、テンプレートの accent2 カラーをフォールバックとしてハードコード
const HEADER_FILL_FALLBACK = { type: 'solid', color: 'ED7D31' };

/**
 * 受領用内示エクセルを作成するためのユースケース
 */
export class DownloadForecastForReceiptExcelUseCase {
  /**
   * 1) B列～L列の書き込み定義をまとめておく
   *    (ヘッダー行を書くなら headerRow で指定)
   *    NOTE: 翻訳を実行時に評価するためメソッドとして定義
   */
  private static getColumnMappings(): IColumnMapping[] {
    return [
      { dataKey: 'customerNumber', colIndex: 2, headerLabel: t('ForecastExcel_CustomerCode'), headerRow: 15 },
      { dataKey: 'customerName', colIndex: 3, headerLabel: t('ForecastExcel_CustomerName'), headerRow: 15 },
      { dataKey: 'supplierNumber', colIndex: 4, headerLabel: t('ForecastExcel_SupplierCode'), headerRow: 15 },
      { dataKey: 'supplierName', colIndex: 5, headerLabel: t('ForecastExcel_SupplierName'), headerRow: 15 },
      { dataKey: 'package', colIndex: 6, headerLabel: t('ForecastExcel_Capacity'), headerRow: 15 },
      { dataKey: 'businessNumber', colIndex: 7, headerLabel: t('ForecastExcel_ProductCode'), headerRow: 15 },
      { dataKey: 'warehouseNumber', colIndex: 8, headerLabel: t('ForecastExcel_WarehouseCode'), headerRow: 15 },
      { dataKey: 'warehouse', colIndex: 9, headerLabel: t('ForecastExcel_WarehouseName'), headerRow: 15 },
      { dataKey: 'salesDestinationNumber', colIndex: 10, headerLabel: t('ForecastExcel_ShipToCode'), headerRow: 15 },
      { dataKey: 'salesDestinationName', colIndex: 11, headerLabel: t('ForecastExcel_ShipToName'), headerRow: 15 },
      { dataKey: 'businessName', colIndex: 12, headerLabel: t('ForecastExcel_ProductName'), headerRow: 15 },
      { dataKey: 'forecastForReceiptMemo', colIndex: 13, headerLabel: '', headerRow: 15 }, // NOTE: ヘッダー名は現状の仕様だと空に
    ];
  }

  private MonthStartColumnIndex = 14;

  /**
   * 受領用内示エクセルを生成し、ZIPとしてまとめた Blob を返却する関数。
   * @param excelData - エクセル作成に必要なデータ
   * @param startYearMonth - 出力開始年月
   * @returns ZIP ファイルの Blob
   */
  async Execute(excelData: OutputForecastForReceiptExcelData, startYearMonth: IYearMonth): Promise<Blob> {
    // ZIPインスタンスを準備
    const zip = new JSZip();

    // excelInputInfo のキーごとに Excel を作成
    const keys = Object.keys(excelData.excelInputInfo);
    for (const key of keys) {
      // テンプレート(Blob)を読込
      const workbook = await XlsxPopulate.fromDataAsync(excelData.templateForecastForReceiptExcelData);
      const sheet = workbook.sheet(0);

      // 15行目の高さを取得（ヘッダーの高さ）
      const headerHeight = sheet.row(15).height() ?? 20; // Default height if not set

      // --------------------------------------------------
      // 1) 行14, 15行目の N列以降に年・月をヘッダーとして書き込む
      // --------------------------------------------------
      const outputRange = excelData.excelInputInfo[key].outputRange;

      // ヘッダー列の開始列番号（B列）
      const headerStartCol = 2;

      // B15セルの塗りつぶしスタイルを取得して年月セルに適用
      // NOTE: xlsx-populate は .style() を呼んだ時点でセルのスタイルを初期化するため、読み取り後にB15へ再適用
      const headerCellFillStyle = sheet.cell(15, headerStartCol).style('fill') || HEADER_FILL_FALLBACK;

      for (let i = 0; i < outputRange; i++) {
        const ym = DateService.updateYearMonth(startYearMonth.year, startYearMonth.month, i);
        const targetColIndex = this.MonthStartColumnIndex + i;
        sheet.cell(14, targetColIndex).value(ym.updateYear); // 年を数値で出力

        // 枠線
        sheet.cell(14, targetColIndex).style('border', { style: 'thin' });
        sheet.cell(15, targetColIndex).style('border', { style: 'thin' });

        // 年・月セルにB15セルと同じ塗りつぶしスタイルを適用
        sheet.cell(14, targetColIndex).style('fill', headerCellFillStyle);
        sheet.cell(15, targetColIndex).style('fill', headerCellFillStyle);

        sheet.cell(15, targetColIndex).value(ym.updateMonth); // 月を数値で出力
      }

      // --------------------------------------------------
      // 2) "B列～M列"のヘッダーを書き込み
      //    ヘッダー書き込み位置を columnMappings から取得
      //    スタイル（塗りつぶし・枠線）も統一する
      // --------------------------------------------------
      // B14に「※変更不可」を書き込む
      sheet.cell(14, headerStartCol).value(t('※変更不可'));

      const columnMappings = DownloadForecastForReceiptExcelUseCase.getColumnMappings();
      columnMappings.forEach((mapping) => {
        if (mapping.headerRow) {
          // 翻訳されたヘッダーラベルを書き込む
          sheet.cell(mapping.headerRow, mapping.colIndex).value(mapping.headerLabel);
          // 値を書き込むとテンプレートのテーマカラーが消えるため、ヘッダー背景色を再適用
          sheet.cell(mapping.headerRow, mapping.colIndex).style('fill', headerCellFillStyle);
        }
      });

      // --------------------------------------------------
      // 4) 行16以降にデータを書き込む
      //    ・columnMappingsの定義に基づいて B～L列をまとめて出力
      // --------------------------------------------------
      const dataList = excelData.excelInputInfo[key].baseBusinessList;
      dataList.forEach((data, idx) => {
        const row = 16 + idx; // 16行目から順番に書く

        // マッピングに従って各列に data の値を書き込む
        columnMappings.forEach((mapping) => {
          sheet.cell(row, mapping.colIndex).value(data[mapping.dataKey]);
        });
      });

      // --------------------------------------------------
      // 5) スタイル調整（16行目以降）
      //    ・枠線を付ける
      //    ・行の高さをヘッダーと同じにする
      // --------------------------------------------------
      const userInputStartRow = 16;
      const userInputEndRow = 16 + dataList.length - 1; // データの最終行

      const userInputStartCol = headerStartCol; // B列
      const userInputEndCol = 14 + outputRange - 1; // 末端

      sheet.range(userInputStartRow, userInputStartCol, userInputEndRow, userInputEndCol).style('border', { style: 'thin' });
      for (let i = userInputStartRow; i <= userInputEndRow; i++) {
        sheet.row(i).height(headerHeight); // ヘッダーと同じ高さにする
      }

      // B15の塗りつぶしスタイルを再適用（読み取り時に消えるため）
      sheet.cell(15, headerStartCol).style('fill', headerCellFillStyle);

      // --------------------------------------------------
      // 6) workbook をバイナリ化して Zip に詰める
      // --------------------------------------------------
      const excelArrayBuffer = await workbook.outputAsync();
      const fileName = `${t('内示')}_${key}.xlsx`;
      zip.file(fileName, excelArrayBuffer);
    }

    // 最後にZipデータを生成 (Blob形式)
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    return zipBlob;
  }
}
